/**
 * AwarenessCursor — 원격 사용자 커서 동기화
 *
 * Local caret → Y.RelativePosition → awareness broadcast
 * Remote awareness → absolute position → DOM overlay
 */
import * as Y from 'yjs'
import { CaretOverlay } from './caret_overlay'
import { getTextOffset } from './color_utils'

export class AwarenessCursor {
  /**
   * @param {Object} options
   * @param {import('y-protocols/awareness').Awareness} options.awareness
   * @param {Y.Doc} options.doc
   * @param {Object} options.editor - EditorJS 인스턴스
   * @param {HTMLElement} options.holderElement - 에디터 컨테이너
   */
  constructor({ awareness, doc, editor, holderElement }) {
    this.awareness = awareness
    this.doc = doc
    this.editor = editor
    this.holderElement = holderElement
    this.overlay = new CaretOverlay({ container: holderElement })
    this.destroyed = false

    this._setupLocalCaretTracking()
    this._setupRemoteCaretRendering()
  }

  destroy() {
    this.destroyed = true
    document.removeEventListener('selectionchange', this._selectionHandler)

    if (this._compositionStartHandler) {
      this.holderElement.removeEventListener('compositionstart', this._compositionStartHandler)
      this.holderElement.removeEventListener('compositionupdate', this._compositionUpdateHandler)
      this.holderElement.removeEventListener('compositionend', this._compositionEndHandler)
    }
    if (this._textareaInputHandler) {
      this.holderElement.removeEventListener('input', this._textareaInputHandler, true)
    }
    if (this._caretDebounceTimer) clearTimeout(this._caretDebounceTimer)
    if (this._compositionThrottleTimer) clearTimeout(this._compositionThrottleTimer)

    this.awareness.off('change', this._awarenessChangeHandler)
    this.overlay.destroy()
  }

  // --- Local Caret → Awareness ---

  _setupLocalCaretTracking() {
    this._isComposing = false
    this._caretDebounceTimer = null

    this._selectionHandler = () => {
      if (this.destroyed) return
      if (this._isComposing) return // composition 중에는 커서 고정

      if (this._caretDebounceTimer) clearTimeout(this._caretDebounceTimer)
      this._caretDebounceTimer = setTimeout(() => {
        this._caretDebounceTimer = null
        this._broadcastLocalCaret()
      }, 50)
    }
    document.addEventListener('selectionchange', this._selectionHandler)

    // 한글 IME 커서 처리:
    // Chrome/macOS에서 한글 연속 입력 시 compositionend가 음절마다 발생하지 않음
    // (Chromium by design — 스페이스/영문 전환 시에만 발생)
    // → compositionupdate에서 300ms throttle로 커서 broadcast
    // → selectionchange는 composition 중 차단 (ㅎ→하→한 jitter 방지)
    this._compositionThrottleTimer = null
    this._compositionStartHandler = () => {
      this._isComposing = true
    }
    this._compositionUpdateHandler = () => {
      if (this.destroyed) return
      if (this._compositionThrottleTimer) return
      this._compositionThrottleTimer = setTimeout(() => {
        this._compositionThrottleTimer = null
        this._broadcastLocalCaret()
      }, 300)
    }
    this._compositionEndHandler = () => {
      this._isComposing = false
      if (this._compositionThrottleTimer) {
        clearTimeout(this._compositionThrottleTimer)
        this._compositionThrottleTimer = null
      }
      this._broadcastLocalCaret()
    }
    this.holderElement.addEventListener('compositionstart', this._compositionStartHandler)
    this.holderElement.addEventListener('compositionupdate', this._compositionUpdateHandler)
    this.holderElement.addEventListener('compositionend', this._compositionEndHandler)

    // textarea (code/raw/markdown)에서는 selectionchange가 안 발생하므로 input으로 감지
    this._textareaInputHandler = () => {
      if (this.destroyed) return
      if (this._caretDebounceTimer) clearTimeout(this._caretDebounceTimer)
      this._caretDebounceTimer = setTimeout(() => {
        this._caretDebounceTimer = null
        this._broadcastLocalCaret()
      }, 50)
    }
    this.holderElement.addEventListener('input', this._textareaInputHandler, true)
  }

  _broadcastLocalCaret() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      this.awareness.setLocalStateField('cursor', null)
      return
    }

    const range = selection.getRangeAt(0)

    // 현재 커서가 에디터 안에 있는지 확인
    if (!this.holderElement.contains(range.startContainer)) {
      return
    }

    // textarea 포커스 (code/raw/markdown 블록) — Selection API 대신 selectionStart 사용
    const activeEl = document.activeElement
    if (activeEl?.tagName === 'TEXTAREA') {
      const blockHolder = activeEl.closest('.ce-block')
      if (!blockHolder || !this.holderElement.contains(blockHolder)) return

      this.awareness.setLocalStateField('cursor', {
        blockId: blockHolder.dataset?.id,
        editableIndex: 0,
        offset: activeEl.selectionStart ?? 0,
        anchorOffset: activeEl.selectionEnd ?? 0,
        isCollapsed: activeEl.selectionStart === activeEl.selectionEnd,
        isTextarea: true,
      })
      return
    }

    // contenteditable 블록
    const blockHolder = range.startContainer.closest
      ? range.startContainer.closest('.ce-block')
      : range.startContainer.parentElement?.closest('.ce-block')

    if (!blockHolder) return

    const blockId = blockHolder.dataset?.id
    if (!blockId) return

    // 커서가 위치한 정확한 contenteditable 요소 찾기
    const allEditables = Array.from(blockHolder.querySelectorAll('[contenteditable="true"]'))
    if (allEditables.length === 0) return

    let editableIndex = 0
    let contentEl = allEditables[0]
    for (let i = 0; i < allEditables.length; i++) {
      if (allEditables[i].contains(range.startContainer)) {
        editableIndex = i
        contentEl = allEditables[i]
        break
      }
    }

    const offset = getTextOffset(contentEl, range.startContainer, range.startOffset)
    const anchorOffset = getTextOffset(contentEl, range.endContainer, range.endOffset)

    this.awareness.setLocalStateField('cursor', {
      blockId,
      editableIndex,
      offset,
      anchorOffset,
      isCollapsed: range.collapsed,
    })
  }

  // --- Remote Caret → Overlay ---

  /**
   * 텍스트 동기화 후 원격 caret 위치 재계산.
   * awareness 데이터는 텍스트보다 먼저 도착하므로,
   * 텍스트가 DOM에 반영된 후 이 메서드를 호출하여 caret 위치를 갱신.
   */
  refreshRemoteCarets() {
    if (this.destroyed) return
    const localClientId = this.awareness.clientID
    this.awareness.getStates().forEach((state, clientId) => {
      if (clientId === localClientId) return
      if (!state?.cursor || !state?.user) return
      this._renderRemoteCaret(clientId, state)
    })
  }

  _setupRemoteCaretRendering() {
    this._awarenessChangeHandler = ({ added, updated, removed }) => {
      if (this.destroyed) return

      const localClientId = this.awareness.clientID

      for (const clientId of removed) {
        if (clientId !== localClientId) {
          this.overlay.removeCaret(clientId)
        }
      }

      for (const clientId of [...added, ...updated]) {
        if (clientId === localClientId) continue
        const state = this.awareness.getStates().get(clientId)
        if (!state?.cursor || !state?.user) continue
        this._renderRemoteCaret(clientId, state)
      }
    }
    this.awareness.on('change', this._awarenessChangeHandler)
  }

  _renderRemoteCaret(clientId, state) {
    if (!state?.cursor || !state?.user) return

    const { blockId, editableIndex = 0, offset, isCollapsed, isTextarea } = state.cursor
    const { name, color } = state.user

    const blockEl = this.holderElement.querySelector(`.ce-block[data-id="${blockId}"]`)
    if (!blockEl) {
      this.overlay.removeCaret(clientId)
      return
    }

    let rect = null

    if (isTextarea) {
      const textarea = blockEl.querySelector('textarea')
      if (textarea) {
        rect = this._getTextareaCaretRect(textarea, offset)
      }
    } else {
      const allEditables = blockEl.querySelectorAll('[contenteditable="true"]')
      const contentEl = allEditables[editableIndex] || allEditables[0]
      if (contentEl) {
        rect = this._getCaretRect(contentEl, offset)
      }
    }

    if (!rect) return
    if (rect.height <= 0 || (rect.top === 0 && rect.left === 0 && rect.width === 0)) return

    this.overlay.updateCaret(clientId, { rect, name, color, blockEl })
  }

  // --- DOM offset utilities ---

  _getCaretRect(contentEl, offset) {
    const walker = document.createTreeWalker(contentEl, NodeFilter.SHOW_TEXT)
    let currentOffset = 0

    while (walker.nextNode()) {
      const textNode = walker.currentNode
      const nodeLength = textNode.textContent.length

      if (currentOffset + nodeLength >= offset) {
        const localOffset = offset - currentOffset
        const range = document.createRange()
        try {
          range.setStart(textNode, Math.min(localOffset, nodeLength))
          range.setEnd(textNode, Math.min(localOffset, nodeLength))
          const rects = range.getClientRects()
          if (rects.length > 0) {
            return rects[0]
          }
          return range.getBoundingClientRect()
        } catch {
          return null
        }
      }

      currentOffset += nodeLength
    }

    // 오프셋을 찾지 못하면 요소 끝
    if (contentEl.childNodes.length > 0) {
      const range = document.createRange()
      range.selectNodeContents(contentEl)
      range.collapse(false)
      return range.getBoundingClientRect()
    }

    return contentEl.getBoundingClientRect()
  }

  /**
   * textarea 내에서 offset 기반 caret 위치 계산
   * (code/raw/markdown 블록용)
   */
  _getTextareaCaretRect(textarea, offset) {
    const taRect = textarea.getBoundingClientRect()
    const style = window.getComputedStyle(textarea)
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2
    const paddingTop = parseFloat(style.paddingTop) || 0
    const paddingLeft = parseFloat(style.paddingLeft) || 0

    const textBefore = (textarea.value || '').slice(0, offset)
    const lines = textBefore.split('\n')
    const currentLine = lines.length - 1
    const currentLineText = lines[lines.length - 1]

    // canvas로 실제 텍스트 폭 측정
    if (!this._measureCtx) {
      this._measureCtx = document.createElement('canvas').getContext('2d')
    }
    this._measureCtx.font = `${style.fontSize} ${style.fontFamily}`
    const textWidth = this._measureCtx.measureText(currentLineText).width

    return {
      top: taRect.top + paddingTop + currentLine * lineHeight - textarea.scrollTop,
      left: taRect.left + paddingLeft + textWidth,
      height: lineHeight,
      width: 0,
    }
  }
}
